import { serializeParams } from '../utils/serializeParams';
import { apiNerisService } from './apiNerisService';
import moment from 'moment';

const endDay = (date) => {
  return date
    ? moment(date, 'YYYY-MM-DD HH:mm:ss', true)
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss')
    : null;
};

export const incidentsApi = {
  getIncidents({
    page = 1,
    searchQuery,
    start_dt,
    end_dt,
    pageSize,
    type,
    status,
    nerisStatus,
    department,
    responseTime,
    employee,
    form_status,
  }) {
    const timezone = start_dt || end_dt ? 0 : null;

    const normalizeDepartment = (department) => {
      if (!department) return null;
      return Array.isArray(department) ? department : [department];
    };

    return apiNerisService
      .get(`/incidents`, {
        params: {
          page,
          search: searchQuery?.length > 0 ? searchQuery : null,
          start_dt,
          end_dt: endDay(end_dt),
          timezone,
          limit: pageSize,

          type,
          status,
          form_status,
          neris_status: nerisStatus,
          department: normalizeDepartment(department),
          ...responseTime,
          employee,
        },
        paramsSerializer: serializeParams,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getIncidentInfo(id) {
    return apiNerisService
      .get(`/incidents/${id}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  deleteIncident(id) {
    return apiNerisService
      .delete(`/incidents/${id}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  uploadIncidentXml(formData) {
    const user = JSON.parse(localStorage.getItem('user'));

    return apiNerisService
      .post(`/upload?user=${user.email || user.fullName || null}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getIncidentsOverview({ start_dt, end_dt, department }) {
    const normalizeDepartment = (department) => {
      if (!department) return null;
      return Array.isArray(department) ? department : [department];
    };

    return apiNerisService
      .get(`/overview`, {
        params: {
          start_dt,
          end_dt: endDay(end_dt),
          department: normalizeDepartment(department),
          timezone: 0,
        },
        paramsSerializer: serializeParams,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getIncidentsCoordinates(dateRange) {
    return apiNerisService
      .get(`/coordinates?time=${dateRange}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getImportLog(page = 1, searchQuery, limit) {
    return apiNerisService
      .get(`/import/log`, {
        params: {
          page,
          search: searchQuery?.length > 0 ? searchQuery : null,
          limit,
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  forceImport(id) {
    return apiNerisService
      .post(`/import/force/${id}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  downloadImport(id) {
    return apiNerisService
      .get(`/import/download/${id}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  syncNeris(id) {
    return apiNerisService
      .post(`/neris/sync/${id}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  /**
   * Retrieves form settings configuration for incidents
   * @param {Object} params - Parameters object
   * @param {string} params.incident - The incident ID (required)
   * @param settings
   */
  formSettings(params, settings = {}) {
    return apiNerisService
      .get(`/form/settings`, {
        params,
        settings,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  saveIncidentsForm({ id, data }) {
    return apiNerisService({
      method: id ? 'patch' : 'post',
      url: `/incident/${id ?? ''}`,
      data,
    })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getDepartmentsList(id) {
    return apiNerisService
      .get(`/departments`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getEmployees(params) {
    return apiNerisService
      .get(`/info/employee`, { params })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getCallStatus() {
    return apiNerisService
      .get(`/info/call-status`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  changeIncidentEmployee(id, employee) {
    return apiNerisService
      .patch(`/incident/${id}/employee`, { employee })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  saveNotes(id, note, noteId = null) {
    return apiNerisService
      .patch(`/incident/${id}/note`, { note, id: noteId })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getNotes(id) {
    return apiNerisService
      .get(`/incident/${id}/notes`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getPropertyDamage(incidentId) {
    return apiNerisService
      .get(`/property/${incidentId}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  savePropertyDamage(incidentId, data) {
    return apiNerisService
      .post(`/property/${incidentId}`, data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getFormSettings(config) {
    return apiNerisService
      .get(`/form/settings/get`, config)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  saveFormSettings(data) {
    return apiNerisService
      .patch(`/form/settings`, data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getTypeFormSettings() {
    return apiNerisService
      .get(`/incident/type/form`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  saveTypeFormSettings(data) {
    return apiNerisService
      .patch(`/incident/type`, data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getAutocompleteOptions(enumClass, searchValue, extraParams = {}) {
    return apiNerisService
      .get(`/info/enum`, {
        params: {
          class: enumClass,
          search: searchValue,
          ...extraParams,
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getIncidentTypeOptions(searchValue) {
    return apiNerisService
      .get(`/incident/type`, {
        params: {
          search: searchValue,
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
  getPermissionRole() {
    return apiNerisService
      .get(`/permission/role`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },

  setPermissionRole(data) {
    return apiNerisService
      .patch(`/permission/role/single`, data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },

  getPermissions(params) {
    return apiNerisService
      .get(`/permission/employee`, {
        params,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },
};
